import type { FC } from 'react';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { ButtonGroup } from '@atlaskit/button';
import { Stack, Text } from '@atlaskit/primitives/compiled';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { ConfluenceTeamAdminControlsMode } from '../useConfluenceSiteTeamConfigurations.tsx';

const i18n = defineMessages({
	mayLoseAccessFrictionModalTitle: {
		id: 'team-admin-controls.friction-modal.lose-some-access-title',
		defaultMessage: 'Member-managed teams will lose access',
		description:
			'Title for the friction modal that some teams may lose access if the friction dialog is confirmed',
	},
	noTeamsFrictionModalTitle: {
		id: 'team-admin-controls.friction-modal.no-teams-title',
		defaultMessage: 'Teams will lose access',
		description:
			'Title for the friction modal that all teams will lose access if the friction dialog is confirmed',
	},
	mayLoseAccessFrictionModalBody: {
		id: 'team-admin-controls.friction-modal.lose-some-access-body',
		defaultMessage:
			'Limiting the use of <b>open teams</b> to may cause unintended loss of access that is already configured across your site.',
		description: 'Body text for the friction modal',
	},
	loseAccessFrictionModalBodyAdditional: {
		id: 'team-admin-controls.friction-modal.lose-some-access-body-additional',
		defaultMessage: '<b>This can’t be undone.</b>',
		description: 'Additional body text for the friction modal',
	},
	willLoseAccessFrictionModalBody: {
		id: 'team-admin-controls.friction-modal.no-teams-body',
		defaultMessage:
			'Any member-managed teams currently assigned access on this site will lose that access.',
		description: 'Body text for the friction modal',
	},
	noTeamsFrictionModalBody: {
		id: 'team-admin-controls.friction-modal.no-teams-body',
		defaultMessage: 'Any teams currently assigned access on this site will lose that access.',
		description: 'Body text for the friction modal',
	},
	noTeamsFrictionModalBodyAdditional: {
		id: 'team-admin-controls.friction-modal.no-teams-body-additional',
		defaultMessage: '<b>This can’t be undone.</b>',
		description: 'Additional body text for the friction modal',
	},
	verifiedTeamsFrictionModalBody: {
		id: 'team-admin-controls.friction-modal.verified-teams-body',
		defaultMessage:
			'Atlassian team is a collection of users that are part of your real-world team. Limiting the use teams to <b>only verified</b> may cause unintended loss of access across your site.',
		description: 'Body text for the friction modal',
	},
	verifiedTeamsFrictionModalBodyAdditional: {
		id: 'team-admin-controls.friction-modal.verified-teams-body-additional',
		defaultMessage: '<b>You can restore this later.</b>',
		description: 'Additional body text for the friction modal',
	},
	frictionModalContinueButton: {
		id: 'team-admin-controls.friction-modal.continue-button',
		defaultMessage: 'Continue',
		description: 'Continue button text for the friction modal',
	},
	frictionModalCancelButton: {
		id: 'team-admin-controls.friction-modal.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Cancel button text for the friction modal',
	},
});

export const FrictionModal: FC<{
	title?: string;
	width?: string | number;
	onConfirm: () => void;
	onClose: () => void;
	teamAccessType: ConfluenceTeamAdminControlsMode;
}> = ({ title, onConfirm, onClose, width = 'small', teamAccessType }) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const modalText = useMemo(() => {
		switch (teamAccessType) {
			case ConfluenceTeamAdminControlsMode.VERIFIED_TEAMS:
				return {
					title: formatMessage(i18n.mayLoseAccessFrictionModalTitle),
					body: formatMessage(i18n.willLoseAccessFrictionModalBody, {
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}),
					bodyAdditional: formatMessage(i18n.loseAccessFrictionModalBodyAdditional, {
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}),
				};
			case ConfluenceTeamAdminControlsMode.NO_TEAMS:
				return {
					title: formatMessage(i18n.noTeamsFrictionModalTitle),
					body: formatMessage(i18n.noTeamsFrictionModalBody, {
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}),
					bodyAdditional: formatMessage(i18n.noTeamsFrictionModalBodyAdditional, {
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}),
				};
			default:
				return {
					title: formatMessage(i18n.mayLoseAccessFrictionModalTitle),
					body: formatMessage(i18n.mayLoseAccessFrictionModalBody, {
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}),
					bodyAdditional: formatMessage(i18n.loseAccessFrictionModalBodyAdditional, {
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}),
				};
		}
	}, [teamAccessType, formatMessage]);

	return (
		<Modal width={width} onClose={onClose}>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance="warning">{title ?? modalText.title}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<Text> {modalText.body}</Text>
					<Text>{modalText.bodyAdditional}</Text>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<ButtonGroup>
					<Button appearance="subtle" onClick={() => onClose()}>
						{formatMessage(i18n.frictionModalCancelButton)}
					</Button>
					<Button appearance="warning" onClick={() => onConfirm()}>
						{formatMessage(i18n.frictionModalContinueButton)}
					</Button>
				</ButtonGroup>
			</ModalFooter>
		</Modal>
	);
};
